/**
 * Calendar panel wrapper — switches between Manual and SelfSchedule calendar modes.
 * Fetches interviewer calendar events and passes them as external events.
 * Exposes CalendarHandle via forwardRef for programmatic navigation (goToDate).
 */
import { forwardRef, useEffect, useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { useCalendarEvents } from '@/api/useCalendarEvents';
import type { CalendarSlot, CalendarHandle } from '@kula-ai/components';
import type { ScheduleMode } from '../../schedule.types';
import { ManualCalendar } from './ManualCalendar';
import { SelfScheduleCalendar } from './SelfScheduleCalendar';

interface CalendarPanelProps {
  mode: ScheduleMode;
  duration: number;
  timezone?: string;
  interviewerIds?: string[];
  meetingRoomIds?: string[];
  interviewName?: string;
  candidateName?: string;
  bookedSlot?: { start: string; end: string; duration?: number } | null;
  onSlotSelect?: (_slot: { start: string; end: string }) => void;
  onTimezoneChange?: (_tz: string) => void;
  onSlotsChange?: (_slots: Array<{ start: string; end: string }>) => void;
  isEdit?: boolean;
}

function getWeekRange(): { start: string; end: string } {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 13); // 2 weeks ahead
  return {
    start: startOfWeek.toISOString(),
    end: endOfWeek.toISOString(),
  };
}

export const CalendarPanel = forwardRef<CalendarHandle, CalendarPanelProps>(
  (
    {
      mode,
      duration,
      timezone,
      interviewerIds = [],
      meetingRoomIds = [],
      interviewName,
      candidateName,
      bookedSlot,
      onSlotSelect,
      onTimezoneChange,
      onSlotsChange,
      isEdit,
    },
    ref
  ) => {
    const isSelfSchedule =
      mode === 'self-schedule' || mode === 'self-schedule-adhoc';

    // Fetch interviewer calendar events for busy time overlay
    const {
      events: calendarEvents,
      fetchEvents,
      cleanup,
    } = useCalendarEvents();

    useEffect(() => {
      if (interviewerIds.length === 0) return;
      const { start, end } = getWeekRange();
      fetchEvents({
        userIds: interviewerIds.map(Number),
        startDate: start,
        endDate: end,
        timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      return cleanup;
    }, [interviewerIds.join(','), timezone]); // eslint-disable-line react-hooks/exhaustive-deps

    // Map calendar events to CalendarSlot format for externalEvents
    const externalEvents = useMemo(
      (): CalendarSlot[] =>
        calendarEvents.map((evt) => ({
          id: evt.id ?? `${evt.start}-${evt.end}`,
          start: evt.start,
          end: evt.end,
          title: evt.title ?? 'Busy',
          backgroundColor: 'var(--kula-colors-secondary-100)',
          borderColor: 'var(--kula-colors-secondary-200)',
          textColor: 'var(--kula-colors-secondary-600)',
        })),
      [calendarEvents]
    );

    return (
      <Flex w="100%" h="100%" minH={0} flexDir="column" overflow="hidden">
        {isSelfSchedule ? (
          <SelfScheduleCalendar
            duration={duration}
            timezone={timezone}
            interviewerIds={interviewerIds}
            meetingRoomIds={meetingRoomIds}
            onSlotsChange={onSlotsChange}
          />
        ) : (
          <ManualCalendar
            ref={ref}
            duration={duration}
            timezone={timezone}
            interviewName={interviewName}
            candidateName={candidateName}
            bookedSlot={bookedSlot}
            externalEvents={externalEvents}
            onSlotSelect={onSlotSelect ?? (() => {})}
            onTimezoneChange={onTimezoneChange}
            isEdit={isEdit}
          />
        )}
      </Flex>
    );
  }
);

CalendarPanel.displayName = 'CalendarPanel';
